import { queryOptions, skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { endpoints } from '@/config/apiEndpoints';
import { collections } from '@/config/databasePaths';
import { FIRESTORE_ENABLED } from '@/config/env';
import { useActiveOrganizationId } from '@/features/auth/useSession';
import {
  type LiveCollection,
  useFirestoreDocument,
  useLiveCollection,
  useLiveMutation,
} from '@/hooks/useLiveData';
import { apiClient } from '@/lib/apiClient';
import { readRecord, readString, requireParsed } from '@/lib/readers';
import {
  type Campaign,
  type CampaignBriefing,
  type CampaignStatus,
  type CampaignWrite,
  isNamedSegment,
  parseCampaign,
  parseCampaignWrite,
} from './types';

/** How many more campaigns each "Show more" asks the query for. */
export const CAMPAIGN_PAGE_SIZE = 24;

/** Every page of the organization's campaigns. It is what a write changes. */
export const campaignScope = (organizationId: string | null) =>
  ['campaigns', organizationId] as const;

/** One page of the organization's campaigns. */
export const campaignsQueryKey = (organizationId: string | null, limit: number) =>
  [...campaignScope(organizationId), 'page', limit] as const;

export interface CampaignPage extends LiveCollection<Campaign> {
  /** Whether the query filled the page it asked for. Another page may follow. */
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
}

/** Returns the organization's campaigns, newest first, one page at a time.
 *  With `enabled` false it opens no listener and no query. */
export function useCampaignPage(enabled = true): CampaignPage {
  const organizationId = useActiveOrganizationId();
  const [limit, setLimit] = useState(CAMPAIGN_PAGE_SIZE);

  const collection = useLiveCollection<Campaign>({
    path: collections.campaigns(organizationId ?? '_'),
    httpPath: endpoints.campaigns.list(),
    queryKey: campaignsQueryKey(organizationId, limit),
    scope: campaignScope(organizationId),
    parse: parseCampaign,
    limit,
    orderByField: 'metadata.updated_at',
    enabled: enabled && Boolean(organizationId),
  });

  return {
    ...collection,
    data: collection.data.filter((campaign) => campaign.isActive),
    hasMore: collection.data.length >= limit,
    loadingMore: collection.loading && collection.data.length > 0,
    loadMore: () => setLimit((current) => current + CAMPAIGN_PAGE_SIZE),
  };
}

/** Returns the query of one campaign by its slug, over HTTP. */
function campaignQuery(organizationId: string | null, slug: string | undefined) {
  return queryOptions({
    queryKey: ['campaign', organizationId, slug] as const,
    enabled: !FIRESTORE_ENABLED,
    queryFn: organizationId && slug
      ? async () => {
          const { id, ...raw } = readRecord(await apiClient.get(endpoints.campaigns.detail(slug)));
          return parseCampaign(readString(id), raw);
        }
      : skipToken,
  });
}

/** Returns one campaign by its slug, read as its own document. */
export function useCampaign(slug: string | undefined): {
  campaign: Campaign | null;
  loading: boolean;
  error: Error | null;
} {
  const organizationId = useActiveOrganizationId();
  const enabled = Boolean(organizationId && slug);

  const live = useFirestoreDocument<Campaign>({
    path: `${collections.campaigns(organizationId ?? '_')}/${slug ?? '_'}`,
    parse: (raw) => parseCampaign(slug ?? '', raw),
    enabled: enabled && FIRESTORE_ENABLED,
  });

  const query = useQuery(campaignQuery(organizationId, slug));

  if (FIRESTORE_ENABLED) return { campaign: live.data, loading: live.loading, error: live.error };

  return { campaign: query.data ?? null, loading: query.isPending && enabled, error: query.error };
}

/** Returns the campaigns whose name or brand matches the search and the two filters. */
export function useFilteredCampaigns(
  campaigns: Campaign[],
  query: string,
  status: CampaignStatus | 'all',
  brandSlug: string | 'all',
  brandNameOf: (slug: string) => string,
): Campaign[] {
  const needle = query.trim().toLowerCase();
  return campaigns.filter(
    (campaign) =>
      (status === 'all' || campaign.status === status) &&
      (brandSlug === 'all' || campaign.brandSlug === brandSlug) &&
      (!needle ||
        campaign.name.toLowerCase().includes(needle) ||
        brandNameOf(campaign.brandSlug).toLowerCase().includes(needle)),
  );
}

/** Sends one campaign write and returns its parsed response. */
async function campaignWrite(request: Promise<unknown>): Promise<CampaignWrite> {
  return requireParsed(parseCampaignWrite(await request), 'campaign write');
}

export interface CampaignCreate {
  name: string;
  brandSlug: string;
}

export interface CampaignUpdate {
  /** Given per call: the first save learns the slug only once the campaign is stored. */
  slug: string;
  name?: string;
  briefing?: CampaignBriefing;
  /** The prefill attempt this save consumed. One consumed is never offered again. */
  appliedExtractionId?: string;
}

function briefingBody(briefing: CampaignBriefing) {
  return {
    goal: briefing.goal,
    starts_on: briefing.startsOn,
    ends_on: briefing.endsOn,
    budget: briefing.budget,
    markets: briefing.markets,
    segments: briefing.segments
      .filter(isNamedSegment)
      .map((segment) => ({
        age_from: segment.ageFrom,
        age_to: segment.ageTo,
        gender: segment.gender,
      })),
    products: briefing.products.map((product) => ({
      product_id: product.productId,
      is_primary: product.isPrimary,
      destination_url: product.destinationUrl,
    })),
    final_url: briefing.finalUrl,
    direction: briefing.direction,
    angles: briefing.angles,
    angle_other: briefing.angleOther,
    treatments: briefing.treatments,
    references: briefing.references.map((reference) => ({
      reference_id: reference.referenceId,
      object_name: reference.objectName,
    })),
    people: briefing.people,
    applied_seasons: briefing.appliedSeasons,
    extracted_fields: briefing.extractedFields,
  };
}

/** Returns the mutation that creates a campaign with its name and brand. */
export function useCreateCampaign() {
  return useMutation({
    mutationFn: ({ name, brandSlug }: CampaignCreate) =>
      campaignWrite(apiClient.post(endpoints.campaigns.create(), { name, brand_slug: brandSlug })),
    meta: { errorKey: 'campaignBriefing.createFailed' },
  });
}

/** Returns the mutation that saves a campaign and writes the saved fields into its cached document. */
export function useUpdateCampaign() {
  const client = useQueryClient();
  const organizationId = useActiveOrganizationId();
  return useMutation({
    mutationFn: async ({ slug, name, briefing, appliedExtractionId }: CampaignUpdate) => {
      return campaignWrite(apiClient.put(endpoints.campaigns.update(slug), {
        ...(name === undefined ? {} : { name }),
        ...(briefing === undefined ? {} : { briefing: briefingBody(briefing) }),
        ...(appliedExtractionId === undefined
          ? {}
          : { applied_extraction_id: appliedExtractionId }),
      }));
    },
    onSuccess: (write, { slug }) =>
      client.setQueryData(campaignQuery(organizationId, slug).queryKey, (current) =>
        current
          ? { ...current, name: write.name, status: write.status, ...(write.briefing && { briefing: write.briefing }) }
          : current,
      ),
  });
}

/** The campaign a write acts on. Without `source` the list shows no row for it. */
export interface CampaignTarget {
  slug: string;
  source?: Campaign;
}

/** Returns the mutation that copies one campaign. The copy shows with the material of its source. */
export function useDuplicateCampaign() {
  const organizationId = useActiveOrganizationId();
  return useLiveMutation({
    queryKey: campaignScope(organizationId),
    kind: 'create',
    mutationFn: ({ slug }: CampaignTarget) =>
      campaignWrite(apiClient.post(endpoints.campaigns.duplicate(slug))),
    rowOf: (body, { source }) =>
      source ? { ...source, id: body.slug, name: body.name, status: 'draft' } : null,
  });
}

/** Returns the mutation that deactivates one campaign and takes it off the list. */
export function useDeactivateCampaign() {
  const organizationId = useActiveOrganizationId();
  return useLiveMutation({
    queryKey: campaignScope(organizationId),
    kind: 'delete',
    mutationFn: ({ slug }: CampaignTarget) =>
      apiClient.delete(endpoints.campaigns.deactivate(slug)),
    rowOf: (_body, { source }) => source ?? null,
  });
}

/** Returns the mutation that asks for the briefing to be read out of an uploaded client brief. */
export function useExtractBriefing(slug: string) {
  return useMutation({
    mutationFn: (briefObject: string) =>
      apiClient.post(endpoints.campaigns.extractBriefing(slug), {
        brief_object: briefObject,
      }),
    meta: { errorKey: 'campaignBriefing.upload.failed' },
  });
}
